import { useContext, useEffect, useState } from 'react';
import { Search, Plus } from 'lucide-react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Modal from '../../components/modal';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import SimpleDropdown from '../../components/SimpleDropdown';
import Switch from '../../components/Switch';
import {
	listUsers,
	createUser,
	updateUser,
	listUserStoreAccess,
	grantUserStoreAccess,
	updateUserStoreAccessRole,
	revokeUserStoreAccess,
} from '../../utils/queries';

const PAGE_SIZE = 20;
// Role is per-store now (user_store_access.role) except PLATFORMADMIN, which
// stays global -- ASSIGNABLE_ROLES mirrors the backend's storeUsers.service.ts
// assignableRoles, the set a per-store access row may hold.
const ASSIGNABLE_ROLES = ['ADMIN', 'USER', 'VIEWER'];
const emptyForm = { email: '', password: '', firstname: '', lastname: '', role: undefined };

function StoreAccessPanel({ user, stores }) {
	const [access, setAccess] = useState([]);
	const [loading, setLoading] = useState(true);
	const [storeId, setStoreId] = useState(stores[0]?.storeId);
	const [grantRole, setGrantRole] = useState('VIEWER');
	const [saving, setSaving] = useState(false);
	const [showGrantForm, setShowGrantForm] = useState(false);

	const refresh = () => {
		setLoading(true);
		return listUserStoreAccess(user.userId).then((result) => {
			if (result?.success) setAccess(result.data);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user.userId]);

	const available = stores.filter((s) => !access.some((a) => a.storeId === s.storeId));

	useEffect(() => {
		if (!available.some((s) => s.storeId === storeId)) setStoreId(available[0]?.storeId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [available.length]);

	// Not a submit button / <form> on purpose: this panel now renders inside
	// the outer "Edit User" <form> (users.js), and nested <form> elements are
	// invalid HTML -- the browser drops the inner one, so a type="submit"
	// button here would actually trigger the outer form's save/close instead
	// of granting access.
	const handleGrant = async () => {
		if (!storeId) return;
		setSaving(true);
		await grantUserStoreAccess(user.userId, storeId, grantRole);
		setSaving(false);
		setShowGrantForm(false);
		refresh();
	};

	const handleRoleChange = async (accessId, role) => {
		await updateUserStoreAccessRole(accessId, role);
		refresh();
	};

	const handleRevoke = async (id) => {
		await revokeUserStoreAccess(id);
		refresh();
	};

	const storeName = (id) => stores.find((s) => s.storeId === id)?.storeName || id;

	if (user.role === 'PLATFORMADMIN') {
		return <p className='text-sm text-gray-400'>Platform Administrators have access to every store - no restriction needed.</p>;
	}

	const columns = [
		{ key: 'storeId', header: 'Store', render: (value) => storeName(value) },
		{
			key: 'role',
			header: 'Role',
			render: (value, row) => (
				<SimpleDropdown
					value={value}
					onChange={(newRole) => handleRoleChange(row.userStoreAccessId, newRole)}
					options={ASSIGNABLE_ROLES.map((r) => ({ value: r, label: r }))}
					className='w-32'
				/>
			),
		},
		{
			key: 'userStoreAccessId',
			header: '',
			sortable: false,
			render: (id) => (
				<button type='button' className='btn-secondary-small' onClick={() => handleRevoke(id)}>
					Revoke
				</button>
			),
		},
	];

	return (
		<div>
			<DataTable columns={columns} data={access} loading={loading} emptyHeading='No store access granted yet' />
			{available.length > 0 && (
				<div className='mt-3'>
					{showGrantForm ? (
						<div className='flex items-end gap-3'>
							<SimpleDropdown
								value={storeId}
								onChange={setStoreId}
								options={available.map((s) => ({ value: s.storeId, label: s.storeName }))}
								className='w-48'
							/>
							<SimpleDropdown
								value={grantRole}
								onChange={setGrantRole}
								options={ASSIGNABLE_ROLES.map((r) => ({ value: r, label: r }))}
								className='w-32'
							/>
							<button type='button' disabled={saving} className='btn-primary-small h-8' onClick={handleGrant}>
								{saving ? 'Granting...' : 'Grant Access'}
							</button>
							<button type='button' className='btn-secondary-small h-8' onClick={() => setShowGrantForm(false)}>
								Cancel
							</button>
						</div>
					) : (
						<button
							type='button'
							className='inline-flex items-center justify-center size-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50'
							onClick={() => setShowGrantForm(true)}
							title='Grant store access'
						>
							<Plus className='size-4' />
						</button>
					)}
				</div>
			)}
		</div>
	);
}

export default function UsersList() {
	const { stores } = useContext(MyContext);
	const [users, setUsers] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	// '' = all users (global + every store), 'none' = global staff only
	// (users with no store access), or an actual storeId to scope to one
	// store's team.
	const [storeFilter, setStoreFilter] = useState('');

	const refresh = () => {
		setLoading(true);
		listUsers({
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			search: search || undefined,
			storeId: storeFilter || undefined,
		}).then((result) => {
			if (result?.success) {
				setUsers(result.data.users);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, search, storeFilter]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setError('');
		setOpen(true);
	};

	const openEdit = (user) => {
		setEditing(user);
		setForm({
			email: user.email,
			firstname: user.firstname,
			lastname: user.lastname,
			role: user.role,
			activeFlag: !!user.activeFlag,
		});
		setError('');
		setOpen(true);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		const result = editing ? await updateUser(editing.userId, form) : await createUser(form);
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		setOpen(false);
		refresh();
	};

	const columns = [
		{
			key: 'email',
			header: 'Email',
			render: (value, row) => (
				<button type='button' className='text-black font-medium hover:underline' onClick={() => openEdit(row)}>
					{value}
				</button>
			),
		},
		{ key: 'firstname', header: 'First Name' },
		{ key: 'lastname', header: 'Last Name' },
		{
			key: 'role',
			header: 'Role',
			// Global role only ever means PLATFORMADMIN-or-not now -- everyone
			// else's actual roles are per-store, shown in the edit modal.
			render: (value) => (value === 'PLATFORMADMIN' ? <StatusBadge status='active' text='Platform Administrator' /> : '—'),
		},
		{
			key: 'activeFlag',
			header: 'Status',
			render: (value) => <StatusBadge status={value ? 'active' : 'inactive'} />,
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Users & Permissions</h1>
				<button type='button' className='btn-primary' onClick={openCreate}>
					New User
				</button>
			</div>

			<div className='mb-5 flex flex-wrap items-end gap-6'>
				<div>
					<label>Search</label>
					<div className='relative max-w-sm'>
						<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
						<input
							className='w-52 form-input pl-9'
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(0);
							}}
						/>
					</div>
				</div>
				<div>
					<label>Filter by Store</label>
					<SimpleDropdown
						value={storeFilter}
						onChange={(v) => {
							setStoreFilter(v);
							setPage(0);
						}}
						options={[
							{ value: 'none', label: 'No store' },						
							{ value: '', label: 'All Stores' },
							...stores.map((s) => ({ value: s.storeId, label: s.storeName })),
						]}
						className='w-48'
					/>
				</div>
			</div>

			<DataTable columns={columns} data={users} loading={loading} emptyHeading='No users yet' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />

			<Modal open={open} setOpen={setOpen} title={editing ? 'Edit User' : 'New User'} size='md'>
				<form onSubmit={handleSubmit} className='space-y-4'>
					{error && <div className='text-sm text-pi-red'>{error}</div>}
					<div>
						<label>Email</label>
						<input
							type='email'
							className='form-input'
							required
							value={form.email}
							onChange={(e) => setForm({ ...form, email: e.target.value })}
						/>
					</div>
					{!editing && (
						<div>
							<label>Password</label>
							<input
								type='password'
								className='form-input'
								required
								value={form.password}
								onChange={(e) => setForm({ ...form, password: e.target.value })}
							/>
						</div>
					)}
					<div className="w-1/2">
						<label>First Name</label>
						<input
							className='form-input'
							required
							value={form.firstname}
							onChange={(e) => setForm({ ...form, firstname: e.target.value })}
						/>
					</div>
					<div className="w-1/2">
						<label>Last Name</label>
						<input
							className='form-input'
							required
							value={form.lastname}
							onChange={(e) => setForm({ ...form, lastname: e.target.value })}
						/>
					</div>
					{/* 'ADMIN' (not undefined) when unchecked -- users.role is NOT NULL,
					    and an update payload with an undefined field gets dropped by
					    Sequelize rather than clearing the column, which would silently
					    leave a demoted user still PLATFORMADMIN in the DB. */}
					<Switch
						checked={form.role === 'PLATFORMADMIN'}
						onChange={(checked) => setForm({ ...form, role: checked ? 'PLATFORMADMIN' : 'ADMIN' })}
						label='Platform Administrator'
					/>
					{editing && (
						<Switch
							checked={form.activeFlag}
							onChange={(checked) => setForm({ ...form, activeFlag: checked })}
							label='Active'
						/>
					)}
					{editing && (
						<div className='border-t border-gray-200 pt-4'>
							<h3 className='text-sm font-medium mb-3'>Store Access</h3>
							<StoreAccessPanel user={editing} stores={stores} />
						</div>
					)}
					<div className='flex gap-3 pt-2'>
						<button type='submit' disabled={saving} className='btn-primary'>
							{saving ? 'Saving...' : 'Save'}
						</button>
						<button type='button' className='btn-secondary' onClick={() => setOpen(false)}>
							Cancel
						</button>
					</div>
				</form>
			</Modal>
		</div>
	);
}
